// src/modules/AudioData.jsx
// Real audio analysis using Web Audio API
// Upload audio → waveform → spectrum → spectrogram → MFCC → band energy → stats

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';
import {
  decodeAudio, extractWaveform, computeSpectrum, computeSpectrogram,
  getAudioStats, getBandEnergy, computeMFCC, generateSyntheticAudio,
} from '../../utils/audioProcessing.js';
import { Panel, Metric, Tag, Spinner, InfoBox, Tabs, timestamp } from '../../components/ui.jsx';

const AUDIO_TABS = [
  { id: 'upload', label: 'UPLOAD', icon: '⬆' },
  { id: 'waveform', label: 'WAVEFORM', icon: '◎', requiresData: true },
  { id: 'spectrum', label: 'SPECTRUM', icon: '⬟', requiresData: true },
  { id: 'spectrogram', label: 'SPECTROGRAM', icon: '◈', requiresData: true },
  { id: 'bands', label: 'FREQ BANDS', icon: '⬡', requiresData: true },
  { id: 'mfcc', label: 'MFCC', icon: '◉', requiresData: true },
  { id: 'stats', label: 'STATS', icon: '◫', requiresData: true },
];

const SYNTH_TYPES = [
  { type: 'sine', label: '440Hz Sine Wave', desc: 'Pure tone, single frequency' },
  { type: 'chord', label: 'Major Chord (C-E-G)', desc: 'Three harmonic frequencies' },
  { type: 'noise', label: 'White Noise', desc: 'Random amplitude, flat spectrum' },
  { type: 'sweep', label: 'Frequency Sweep', desc: '100Hz → 3kHz linear sweep' },
  { type: 'pulse', label: 'Square Wave 220Hz', desc: 'Rich harmonic content' },
];

export default function AudioDataModule({ mode }) {
  const [tab, setTab] = useState('upload');
  const [audioBuffer, setAudioBuffer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState('');

  // Analysis data
  const [waveform, setWaveform] = useState([]);
  const [spectrum, setSpectrum] = useState([]);
  const [spectrogram, setSpectrogram] = useState(null);
  const [bandEnergy, setBandEnergy] = useState([]);
  const [mfcc, setMfcc] = useState([]);
  const [stats, setStats] = useState(null);

  // Playback
  const [playing, setPlaying] = useState(false);
  const audioCtxRef = useRef(null);
  const sourceRef = useRef(null);
  const fileRef = useRef();

  // Spectrum controls
  const [fftSize, setFftSize] = useState(512);
  const [specMode, setSpecMode] = useState('magnitude'); // magnitude | db

  const processBuffer = useCallback((buffer, name) => {
    setAudioBuffer(buffer);
    setFileName(name);
    setWaveform(extractWaveform(buffer, 400));
    setSpectrum(computeSpectrum(buffer, fftSize, 100));
    setSpectrogram(computeSpectrogram(buffer, 256, 128, 50));
    setBandEnergy(getBandEnergy(buffer, 1024));
    setMfcc(computeMFCC(buffer, 13));
    setStats(getAudioStats(buffer));
    setTab('waveform');
    setLoading(false);
  }, [fftSize]);

  const handleFile = async (file) => {
    if (!file || !file.type.startsWith('audio/')) {
      alert('Please upload an audio file (MP3, WAV, OGG, etc.)'); return;
    }
    setLoading(true);
    setFileName(file.name);
    try {
      const { audioBuffer } = await decodeAudio(file);
      processBuffer(audioBuffer, file.name);
    } catch (e) {
      console.error('Audio decode error:', e);
      setLoading(false);
    }
  };

  const loadSynthetic = (type) => {
    setLoading(true);
    setTimeout(() => {
      const buffer = generateSyntheticAudio(type, 2);
      processBuffer(buffer, `synth_${type}.wav`);
    }, 100);
  };

  // Recompute spectrum when fftSize changes
  useEffect(() => {
    if (!audioBuffer) return;
    setSpectrum(computeSpectrum(audioBuffer, fftSize, 100));
  }, [fftSize, audioBuffer]);

  const playAudio = () => {
    if (!audioBuffer) return;
    if (playing) { sourceRef.current?.stop(); setPlaying(false); return; }
    const ctx = new AudioContext();
    audioCtxRef.current = ctx;
    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(ctx.destination);
    source.start();
    source.onended = () => setPlaying(false);
    sourceRef.current = source;
    setPlaying(true);
  };

  useEffect(() => () => { sourceRef.current?.stop(); }, []);

  // Spectrogram color map
  const spectroColor = (value, max = 80) => {
    const t = Math.min(1, value / max);
    if (t < 0.25) return `rgb(${Math.floor(t*4*5)}, 0, ${Math.floor(50 + t*4*100)})`;
    if (t < 0.5) return `rgb(0, ${Math.floor((t-0.25)*4*150)}, ${Math.floor(200 - (t-0.25)*4*100)})`;
    if (t < 0.75) return `rgb(${Math.floor((t-0.5)*4*255)}, 255, 0)`;
    return `rgb(255, ${Math.floor(255 - (t-0.75)*4*200)}, 0)`;
  };

  return (
    <div className="anim-fadeup" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <Tabs tabs={AUDIO_TABS} active={tab} onChange={setTab} dataset={audioBuffer} />

      {/* ══ UPLOAD ══ */}
      {tab === 'upload' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <div
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={e => { e.preventDefault(); setIsDragging(false); handleFile(e.dataTransfer.files[0]); }}
            onClick={() => fileRef.current?.click()}
            style={{ border: `2px dashed ${isDragging ? 'var(--violet)' : 'var(--b1)'}`, borderRadius: '4px', padding: '52px 24px', textAlign: 'center', cursor: 'pointer', background: isDragging ? 'var(--gviolet)' : 'var(--surface)', transition: 'all .2s', animation: 'float 3s ease infinite' }}>
            <input ref={fileRef} type="file" accept="audio/*" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
            {loading
              ? <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}><Spinner size={24} /><span style={{ color: 'var(--violet)', fontSize: '12px' }}>DECODING AUDIO...</span></div>
              : <>
                  <div style={{ fontSize: '40px', marginBottom: '14px', opacity: .4 }}>◎</div>
                  <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '18px', color: isDragging ? 'var(--violet)' : 'var(--t1)', marginBottom: '7px' }}>Drop Audio File Here</div>
                  <div style={{ fontSize: '12px', color: 'var(--t3)', marginBottom: '18px' }}>MP3, WAV, OGG, FLAC, AAC supported</div>
                </>}
          </div>

          <Panel title="SYNTHETIC AUDIO GENERATORS" accent="var(--violet)">
            <div style={{ fontSize: '11px', color: 'var(--t3)', marginBottom: '12px' }}>Generate test signals to explore DSP without uploading a file:</div>
            {SYNTH_TYPES.map(({ type, label, desc }) => (
              <div key={type} onClick={() => loadSynthetic(type)}
                style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px', border: '1px solid var(--b0)', borderRadius: '3px', marginBottom: '6px', cursor: 'pointer', background: 'var(--raised)', transition: 'all .15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--violet)'; e.currentTarget.style.background = 'var(--hover)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--b0)'; e.currentTarget.style.background = 'var(--raised)'; }}>
                <div style={{ width: '36px', height: '36px', background: 'var(--gviolet)', border: '1px solid var(--violet)33', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', color: 'var(--violet)', flexShrink: 0 }}>◎</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', color: 'var(--t1)', fontWeight: 600 }}>{label}</div>
                  <div style={{ fontSize: '10px', color: 'var(--t3)' }}>{desc} · 2s · 44.1kHz</div>
                </div>
                <span style={{ color: 'var(--violet)', fontSize: '12px' }}>→</span>
              </div>
            ))}
          </Panel>
        </div>
      )}

      {/* ══ WAVEFORM ══ */}
      {tab === 'waveform' && waveform.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {stats && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' }}>
              <Metric label="Duration" value={stats.duration} unit="s" color="var(--violet)" />
              <Metric label="Sample Rate" value={(stats.sampleRate / 1000).toFixed(1)} unit="kHz" color="var(--cyan)" />
              <Metric label="Peak" value={stats.peak} color="var(--red)" sub={`${stats.dbPeak} dB`} />
              <Metric label="RMS" value={stats.rms} color="var(--green)" sub={`${stats.dbRMS} dB`} />
              <Metric label="Zero Crossings" value={(stats.zcRate / 1000).toFixed(1)} unit="k/s" color="var(--gold)" />
            </div>
          )}

          <Panel title="WAVEFORM" badge={fileName} accent="var(--violet)">
            <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', alignItems: 'center' }}>
              <button onClick={playAudio} style={{ padding: '7px 18px', background: playing ? 'var(--gred)' : 'var(--gviolet)', border: `1px solid ${playing ? 'var(--red)' : 'var(--violet)'}`, color: playing ? 'var(--red)' : 'var(--violet)', fontSize: '10px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono', letterSpacing: '.08em' }}>
                {playing ? '⏹ STOP' : '▶ PLAY'}
              </button>
              <span style={{ fontSize: '10px', color: 'var(--t3)' }}>Amplitude over time · 400 samples</span>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={waveform}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                <XAxis dataKey="sample" stroke="var(--t3)" tick={{ fontSize: 9 }} label={{ value: 'Sample', position: 'insideBottom', offset: -2, fill: 'var(--t3)', fontSize: 10 }} />
                <YAxis stroke="var(--t3)" tick={{ fontSize: 9 }} domain={[-1, 1]} />
                <Tooltip />
                <Area type="monotone" dataKey="amp" stroke="var(--violet)" fill="var(--violet)" fillOpacity={0.2} strokeWidth={1} />
              </AreaChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title="PEAK vs TROUGH" accent="var(--violet)">
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={waveform.filter((_, i) => i % 4 === 0)}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                <XAxis dataKey="sample" stroke="var(--t3)" tick={{ fontSize: 9 }} />
                <YAxis stroke="var(--t3)" tick={{ fontSize: 9 }} domain={[-1, 1]} />
                <Tooltip />
                <Area type="monotone" dataKey="peak" stroke="var(--red)" fill="var(--red)" fillOpacity={0.15} strokeWidth={1} name="Peak" />
                <Area type="monotone" dataKey="trough" stroke="var(--cyan)" fill="var(--cyan)" fillOpacity={0.15} strokeWidth={1} name="Trough" />
                <Legend />
              </AreaChart>
            </ResponsiveContainer>
          </Panel>
        </div>
      )}

      {/* ══ SPECTRUM ══ */}
      {tab === 'spectrum' && spectrum.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '10px', color: 'var(--t3)', marginBottom: '5px' }}>FFT SIZE</div>
              <select value={fftSize} onChange={e => setFftSize(parseInt(e.target.value))} style={{ background: 'var(--deep)', border: '1px solid var(--b1)', color: 'var(--t1)', padding: '6px 10px', fontSize: '11px', fontFamily: 'JetBrains Mono', borderRadius: '2px' }}>
                {[128, 256, 512, 1024, 2048].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: '10px', color: 'var(--t3)', marginBottom: '5px' }}>Y-AXIS</div>
              <div style={{ display: 'flex', gap: '2px' }}>
                {['magnitude', 'db'].map(m => (
                  <button key={m} onClick={() => setSpecMode(m)} style={{ padding: '6px 12px', border: `1px solid ${specMode === m ? 'var(--violet)' : 'var(--b0)'}`, background: specMode === m ? 'var(--gviolet)' : 'transparent', color: specMode === m ? 'var(--violet)' : 'var(--t2)', fontSize: '10px', cursor: 'pointer', fontFamily: 'JetBrains Mono', borderRadius: '2px' }}>{m}</button>
                ))}
              </div>
            </div>
            {stats && <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
              <Tag color="var(--violet)">Nyquist: {(stats.nyquist / 1000).toFixed(1)}kHz</Tag>
              <Tag color="var(--cyan)">Res: {(stats.sampleRate / fftSize).toFixed(1)}Hz/bin</Tag>
            </div>}
          </div>

          <Panel title="FREQUENCY SPECTRUM" badge={`FFT=${fftSize}`} accent="var(--violet)">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={spectrum}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                <XAxis dataKey="freq" stroke="var(--t3)" tick={{ fontSize: 9 }} label={{ value: 'Frequency (Hz)', position: 'insideBottom', offset: -2, fill: 'var(--t3)', fontSize: 10 }} />
                <YAxis stroke="var(--t3)" tick={{ fontSize: 9 }} />
                <Tooltip formatter={(v) => [specMode === 'db' ? `${v} dB` : v.toFixed(5), specMode === 'db' ? 'dB' : 'Magnitude']} />
                <Bar dataKey={specMode} fill="var(--violet)" opacity={0.85} radius={[1, 1, 0, 0]}>
                  {spectrum.map((_, i) => <Cell key={i} fill={`hsl(${180 + i * (60/spectrum.length)}, 70%, 60%)`} opacity={0.85} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title="LOG-SCALE SPECTRUM VIEW" accent="var(--violet)">
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={spectrum}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                <XAxis dataKey="freq" stroke="var(--t3)" tick={{ fontSize: 9 }} scale="log" domain={['auto', 'auto']} />
                <YAxis stroke="var(--t3)" tick={{ fontSize: 9 }} />
                <Tooltip />
                <Line type="monotone" dataKey="magnitude" stroke="var(--cyan)" strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Panel>
        </div>
      )}

      {/* ══ SPECTROGRAM ══ */}
      {tab === 'spectrogram' && spectrogram && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <InfoBox color="var(--violet)" icon="◈">
            Spectrogram shows frequency energy over time. X-axis = time, Y-axis = frequency bins, color intensity = energy level. Computed with FFT={spectrogram.fftSize}, Hanning window.
          </InfoBox>

          <Panel title="SPECTROGRAM" badge={`${spectrogram.matrix.length} frames × ${spectrogram.freqBins} freq bins`} accent="var(--violet)">
            <div style={{ overflowX: 'auto' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${spectrogram.matrix.length}, 1fr)`,
                gridTemplateRows: `repeat(${spectrogram.freqBins}, 6px)`,
                gap: '1px',
                minWidth: `${spectrogram.matrix.length * 12}px`,
                height: `${spectrogram.freqBins * 7}px`,
              }}>
                {spectrogram.matrix.map((frame, col) =>
                  [...frame.col_data].reverse().map((energy, row) => (
                    <div key={`${col}-${row}`}
                      style={{ background: spectroColor(energy), width: '100%' }}
                      title={`t=${frame.time}s freq_bin=${spectrogram.freqBins - 1 - row} energy=${energy.toFixed(1)}dB`}
                    />
                  ))
                )}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '10px', color: 'var(--t3)' }}>
              <span>t=0s</span>
              <span style={{ color: 'var(--violet)' }}>← Time →</span>
              <span>t={stats?.duration}s</span>
            </div>
            <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '10px', color: 'var(--t3)' }}>Energy scale:</span>
              {[0, 20, 40, 60, 80].map(v => (
                <div key={v} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <div style={{ width: '14px', height: '14px', background: spectroColor(v), borderRadius: '2px' }} />
                  <span style={{ fontSize: '9px', color: 'var(--t3)' }}>{v}dB</span>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ══ FREQ BANDS ══ */}
      {tab === 'bands' && bandEnergy.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <Panel title="FREQUENCY BAND ENERGY" accent="var(--violet)">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={bandEnergy} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" horizontal={false} />
                <XAxis type="number" stroke="var(--t3)" tick={{ fontSize: 9 }} />
                <YAxis type="category" dataKey="name" stroke="var(--t3)" tick={{ fontSize: 10 }} width={80} />
                <Tooltip />
                <Bar dataKey="energy" radius={[0, 2, 2, 0]}>
                  {bandEnergy.map((b, i) => <Cell key={i} fill={b.color} opacity={0.85} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Panel>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Panel title="BAND BREAKDOWN">
              {bandEnergy.map(band => {
                const maxE = Math.max(...bandEnergy.map(b => b.energy));
                const pct = maxE > 0 ? (band.energy / maxE) * 100 : 0;
                return (
                  <div key={band.name} style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <div>
                        <span style={{ fontSize: '12px', color: band.color, fontWeight: 600 }}>{band.name}</span>
                        <span style={{ fontSize: '10px', color: 'var(--t3)', marginLeft: '8px' }}>{band.lo}-{band.hi}Hz</span>
                      </div>
                      <span style={{ fontSize: '11px', color: band.color }}>{band.energy}</span>
                    </div>
                    <div style={{ height: '5px', background: 'var(--b0)', borderRadius: '3px' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: band.color, borderRadius: '3px', transition: 'width .5s' }} />
                    </div>
                  </div>
                );
              })}
            </Panel>

            <Panel title="BAND RADAR">
              <ResponsiveContainer width="100%" height={200}>
                <RadarChart data={bandEnergy.map(b => ({ subject: b.name.split(' ')[0], energy: b.energy }))}>
                  <PolarGrid stroke="var(--b1)" />
                  <PolarAngleAxis dataKey="subject" tick={{ fill: 'var(--t3)', fontSize: 9 }} />
                  <PolarRadiusAxis tick={{ fill: 'var(--t3)', fontSize: 8 }} />
                  <Radar dataKey="energy" stroke="var(--violet)" fill="var(--violet)" fillOpacity={0.25} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </Panel>
          </div>
        </div>
      )}

      {/* ══ MFCC ══ */}
      {tab === 'mfcc' && mfcc.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <InfoBox color="var(--violet)" icon="◈">
            Mel-Frequency Cepstral Coefficients (MFCCs) represent the spectral envelope of audio. Widely used in speech recognition, music classification, and audio fingerprinting. Computed via DCT of log-mel spectrum.
          </InfoBox>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <Panel title="MFCC COEFFICIENTS" badge="13 coeffs" accent="var(--violet)">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={mfcc}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                  <XAxis dataKey="coeff" stroke="var(--t3)" tick={{ fontSize: 10 }} label={{ value: 'Coefficient Index', position: 'insideBottom', offset: -2, fill: 'var(--t3)', fontSize: 10 }} />
                  <YAxis stroke="var(--t3)" tick={{ fontSize: 9 }} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                    {mfcc.map((c, i) => <Cell key={i} fill={c.value >= 0 ? 'var(--cyan)' : 'var(--red)'} opacity={0.85} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="COEFFICIENT VALUES">
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead><tr style={{ background: 'var(--deep)' }}>
                    <th style={{ padding: '7px 12px', textAlign: 'left', color: 'var(--t3)', fontSize: '10px', borderBottom: '1px solid var(--b0)' }}>COEFF</th>
                    <th style={{ padding: '7px 12px', textAlign: 'left', color: 'var(--t3)', fontSize: '10px', borderBottom: '1px solid var(--b0)' }}>VALUE</th>
                    <th style={{ padding: '7px 12px', textAlign: 'left', color: 'var(--t3)', fontSize: '10px', borderBottom: '1px solid var(--b0)' }}>BAR</th>
                    <th style={{ padding: '7px 12px', textAlign: 'left', color: 'var(--t3)', fontSize: '10px', borderBottom: '1px solid var(--b0)' }}>INTERPRETATION</th>
                  </tr></thead>
                  <tbody>
                    {mfcc.map(({ coeff, value }) => {
                      const INTERPRETATIONS = ['Energy', 'Spectral Centroid', 'Bandwidth', 'Spectral Flux', 'Brightness', 'Flatness', 'Rolloff', 'Entropy', 'Spread', 'Kurtosis', 'Skewness', 'Crest', 'RMS Env'];
                      return (
                        <tr key={coeff} style={{ borderBottom: '1px solid var(--b0)' }}>
                          <td style={{ padding: '6px 12px', color: 'var(--violet)', fontWeight: 600 }}>C{coeff}</td>
                          <td style={{ padding: '6px 12px', color: value >= 0 ? 'var(--cyan)' : 'var(--red)', fontWeight: 600 }}>{value}</td>
                          <td style={{ padding: '6px 12px', width: '120px' }}>
                            <div style={{ height: '6px', background: 'var(--b0)', borderRadius: '3px' }}>
                              <div style={{ height: '100%', width: `${Math.min(100, Math.abs(value) * 200)}%`, background: value >= 0 ? 'var(--cyan)' : 'var(--red)', borderRadius: '3px' }} />
                            </div>
                          </td>
                          <td style={{ padding: '6px 12px', color: 'var(--t3)', fontSize: '10px' }}>{INTERPRETATIONS[coeff] || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        </div>
      )}

      {/* ══ STATS ══ */}
      {tab === 'stats' && stats && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
            <Metric label="Duration" value={stats.duration} unit="s" color="var(--violet)" />
            <Metric label="Sample Rate" value={stats.sampleRate.toLocaleString()} unit="Hz" color="var(--cyan)" />
            <Metric label="Channels" value={stats.channels} color="var(--green)" />
            <Metric label="Total Samples" value={stats.samples} color="var(--t2)" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
            <Metric label="Peak Amplitude" value={stats.peak} color="var(--red)" sub={`${stats.dbPeak} dBFS`} />
            <Metric label="RMS Level" value={stats.rms} color="var(--gold)" sub={`${stats.dbRMS} dBFS`} />
            <Metric label="Zero Cross Rate" value={stats.zcRate.toLocaleString()} unit="/s" color="var(--violet)" sub="Pitch indicator" />
            <Metric label="Nyquist Freq" value={(stats.nyquist / 1000).toFixed(1)} unit="kHz" color="var(--cyan)" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <Panel title="AUDIO METADATA">
              {[
                ['File', fileName], ['Bit Depth', stats.bitDepth], ['Sample Rate', `${stats.sampleRate} Hz`],
                ['Duration', `${stats.duration}s`], ['Nyquist', `${(stats.nyquist/1000).toFixed(1)} kHz`],
                ['dBFS Peak', `${stats.dbPeak} dB`], ['dBFS RMS', `${stats.dbRMS} dB`],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--b0)' }}>
                  <span style={{ fontSize: '11px', color: 'var(--t2)' }}>{k}</span>
                  <span style={{ fontSize: '11px', color: 'var(--t1)', fontWeight: 500 }}>{v}</span>
                </div>
              ))}
            </Panel>

            <Panel title="DYNAMIC RANGE ANALYSIS">
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '10px', color: 'var(--t3)', marginBottom: '6px' }}>CREST FACTOR (Peak / RMS)</div>
                <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '28px', color: 'var(--violet)' }}>
                  {(parseFloat(stats.peak) / parseFloat(stats.rms)).toFixed(1)}x
                </div>
                <div style={{ fontSize: '10px', color: 'var(--t3)' }}>
                  {parseFloat(stats.peak) / parseFloat(stats.rms) > 10 ? 'High dynamic range (transient-heavy)' : parseFloat(stats.peak) / parseFloat(stats.rms) > 4 ? 'Normal dynamic range' : 'Low dynamic range (heavily compressed)'}
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '10px', color: 'var(--t3)', marginBottom: '6px' }}>LEVEL METERS</div>
                {[['Peak', parseFloat(stats.dbPeak), 'var(--red)'], ['RMS', parseFloat(stats.dbRMS), 'var(--green)']].map(([label, val, color]) => {
                  const pct = Math.max(0, Math.min(100, (val + 60) / 60 * 100));
                  return (
                    <div key={label} style={{ marginBottom: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                        <span style={{ fontSize: '10px', color: 'var(--t2)' }}>{label}</span>
                        <span style={{ fontSize: '10px', color }}>{val} dBFS</span>
                      </div>
                      <div style={{ height: '6px', background: 'var(--b0)', borderRadius: '3px' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '3px' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Panel>
          </div>
        </div>
      )}
    </div>
  );
}
